import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardPath } from '../../utils/navigationUtils';
import logo from "../../assets/img/logo.png";
import { toast } from "react-toastify";
import { SignupModal } from './SignupModal';
// import { Label } from '../ui/Label';

export const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [showSignupModal, setShowSignupModal] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();
    // const location = useLocation();

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!email) {
            newErrors.email = 'Please enter your email address.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!password) {
            newErrors.password = 'Please enter your password.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'email') {
            if (!value) {
                newErrors.email = 'Please enter your email address.';
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                newErrors.email = 'Please enter a valid email address.';
            } else {
                delete newErrors.email;
            }
        } else if (fieldName === 'password') {
            if (!value) {
                newErrors.password = 'Please enter your password.';
            } else {
                delete newErrors.password;
            }
        }

        setErrors(newErrors);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            const authedUser = await login(email, password);

            const userRole = authedUser?.role;
            // User logged in successfully
            const redirectPath = getDashboardPath(userRole);
            // Redirecting to appropriate dashboard
            toast.success("Login successful!");
            navigate(redirectPath, { replace: true });

        } catch (error: any) {
            const errorMessage = error.message || 'Login failed. Please try again.';
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };



    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <>
            <div className='auth-wrapper'>
                <div className='auth-box'>
                    <div className='auth-header text-center'>
                        <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                        <h1 className='mb-2'>Sign In with Email</h1>
                        <p className='text-16-reg grey-500 mb-4'>Sign in to access smart SOP guidance in the field.</p>
                        <svg className='mx-auto mb-4' width="129" height="5" viewBox="0 0 129 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" width="81" height="5" rx="2.5" fill="#DA291C" />
                            <rect x="88.5" width="22" height="5" rx="2.5" fill="#BDBDBD" />
                        </svg>
                    </div>
                    <div className="auth-body">
                        <form onSubmit={handleSubmit}>
                            {/* Email Field */}
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    id="email"
                                    type="text"
                                    autoComplete="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        // Real-time validation
                                        validateField('email', e.target.value);
                                    }}
                                    className={`form-control ${errors.email ? 'border-danger' : ''}`}
                                />
                                {errors.email && (
                                    <p className="error-message">{errors.email}</p>
                                )}
                            </div>
                            {/* Password Field */}
                            <div className="form-group password-feild">
                                <label htmlFor="password">Password</label>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        // Real-time validation
                                        validateField('password', e.target.value);
                                    }}
                                    className={`form-control ${errors.password ? 'border-danger' : ''}`}
                                />
                                <button className='password-toggle' type="button" onClick={togglePasswordVisibility} >
                                    {showPassword ? (
                                        <i className="iconoir-eye"></i>
                                    ) : (
                                        <i className="iconoir-eye-closed"></i>
                                    )}
                                </button>
                                {errors.password && (
                                    <p className="error-message">{errors.password}</p>
                                )}
                            </div>

                            {/* Signup and Forgot Password Links - FOR TESTING ONLY */}
                            {/* <div className="flex justify-between items-center">
                                <button
                                    type="button"
                                    onClick={() => setShowSignupModal(true)}
                                    className='text-16-bold color-renegade hover:underline'
                                >
                                    Sign up
                                </button>
                                <Link className='text-16-bold color-renegade hover:underline' to="/forgot-password">
                                    Forgot password?
                                </Link>
                            </div> */}


                            {/* Forgot Password Link */}
                            <div className="text-right">
                                <Link className='text-16-bold color-renegade' to="/forgot-password">
                                    Forgot password?
                                </Link>
                            </div>

                            {/* Sign In Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className='renegade-btn-primary w-100 mt-5'
                            >
                                {isLoading ? 'Signing In...' : 'Sign In'}
                            </button>

                        </form>
                    </div>
                </div>
            </div>

            {/* Signup Modal */}
            {showSignupModal && (
                <SignupModal
                    onClose={() => setShowSignupModal(false)}
                    onSuccess={() => {
                        setShowSignupModal(false);
                        toast.success("Account created successfully! Please sign in.");
                    }}
                />
            )}
        </>
    );
};
